import React, { useState } from "react";
import {
  Fieldset,
  Legend,
  Field,
  Label,
  Description,
  ErrorMessage,
} from "@/components/fieldset";
import { Input } from "@/components/input";
import { Select } from "@/components/select";
import { Button } from "@/components/button";
import type { ICreateGroupRequest } from "@/apis/groups/createGroup";

interface GroupFormProps {
  initialData?: Partial<ICreateGroupRequest>;
  onSubmit: (data: ICreateGroupRequest) => void;
  onCancel: () => void;
  isLoading?: boolean;
  serverError?: string;
  isEditMode?: boolean;
}

const GroupForm: React.FC<GroupFormProps> = ({
  initialData = {},
  onSubmit,
  onCancel,
  isLoading = false,
  serverError,
  isEditMode = false,
}) => {
  // 그룹 정보 상태 관리
  const [formData, setFormData] = useState<ICreateGroupRequest>({
    name: "",
    status: "active",
    ...initialData,
  });

  // 유효성 검사 오류 상태
  const [errors, setErrors] = useState<Record<string, string>>({});

  // 입력 필드 변경 핸들러
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // 필드가 변경되면 해당 필드의 오류 메시지 제거
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // 폼 제출 핸들러
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // 유효성 검사
    const validationErrors: Record<string, string> = {};

    if (!formData.name) {
      validationErrors.name = "Group name is required";
    }

    // 유효성 검사 오류가 있으면 제출하지 않음
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // 최종 제출 데이터 준비
    const submitData = { ...formData };

    // 제출
    onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* 서버 오류 표시 */}
      {serverError && (
        <div className="p-4 bg-red-50 text-red-600 rounded-md">
          {serverError}
        </div>
      )}

      {/* 기본 정보 */}
      <Fieldset>
        <Legend>Basic Information</Legend>

        <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2">
          <Field>
            <Label htmlFor="name">Group Name*</Label>
            <Input
              id="name"
              name="name"
              placeholder="Enter group name"
              value={formData.name}
              onChange={handleChange}
              aria-invalid={!!errors.name}
            />
            {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
          </Field>
        </div>
      </Fieldset>

      {/* 제출 버튼 */}
      <div className="flex justify-end space-x-4">
        <Button plain onClick={onCancel} type="button">
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading
            ? "Submitting..."
            : isEditMode
            ? "Update Group"
            : "Create Group"}
        </Button>
      </div>
    </form>
  );
};

export default GroupForm;
